/* eslint-disable */
import React from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import AssetValueChartFilter from "v2/features/plans/components/AssetValueChartFilter";
import AssetValueLineChart from "v2/features/plans/components/AssetValueLineChart";
import { ProtectChart, type ProtectYearData } from "./ProtectChart";

export type PerformanceAssetLineChartData = NonNullable<
  React.ComponentProps<typeof AssetValueLineChart>["data"]
>;

export interface PerformanceChartPanelProps {
  isDark: boolean;
  cardBg: string;
  divider: string;
  textSub: string;
  insuredName: string;
  chartBusy: boolean;
  graphDataLength: number;
  performanceLoading: boolean;
  assetLineChartData: PerformanceAssetLineChartData;
  assetChartFilter: string;
  onAssetChartFilterChange: (v: string) => void;
  chartSeriesFilters: string[];
  showProjections: boolean;
  onProjectionsToggle: () => void;
  combinedFirstLineExtras: Record<string, unknown> | undefined;
  trueIqAppendSeries: object[];
  chartGridTop: string;
  trueIqChartMode: boolean;
  tooltipFormatterOverride?: ((params: any) => string) | null;
  showActualValues?: boolean;
  enableAgeFilter?: boolean;
  isBuySell?: boolean;
  /** Per-filter line color / label overrides (used by BS to color lines by value metric). */
  seriesColorMap?: Record<string, string>;
  seriesLabelMap?: Record<string, string>;
  /** When true + protectData provided, replaces the performance chart with ProtectChart (BS only). */
  protectModeOn?: boolean;
  protectData?: ProtectYearData[];
  protectLoading?: boolean;
}

const PerformanceChartPanel = React.memo((props: PerformanceChartPanelProps) => {
  const {
    isDark,
    cardBg,
    divider,
    textSub,
    insuredName,
    chartBusy,
    graphDataLength,
    performanceLoading,
    assetLineChartData,
    assetChartFilter,
    onAssetChartFilterChange,
    chartSeriesFilters,
    showProjections,
    onProjectionsToggle,
    combinedFirstLineExtras,
    trueIqAppendSeries,
    chartGridTop,
    tooltipFormatterOverride,
    showActualValues,
    enableAgeFilter,
    isBuySell,
    seriesColorMap,
    seriesLabelMap,
    protectModeOn,
    protectData,
    protectLoading,
  } = props;

  // BS protect mode: replace the performance chart entirely with ProtectChart
  if (protectModeOn && protectData && protectData?.length > 0) {
    return (
      <div
        className={`overflow-hidden ${
          isDark ? "border-[#3A3A50] shadow-none bg-[#1E2030]" : "border-slate-200/90 bg-white"
        }`}
      >
        <ProtectChart
          data={protectData}
          isDark={isDark}
          showProjections={showProjections}
          xAxisDimension={assetChartFilter}
          loading={protectLoading}
        />
        <div className="mt-1 px-1 pb-2">
          <AssetValueChartFilter
            selectedChartFilter={assetChartFilter}
            onChartFilterChange={onAssetChartFilterChange}
            showProjections={showProjections}
            onProjectionsToggle={onProjectionsToggle}
            enableAgeFilter={enableAgeFilter}
            hideProjection
            overrideCalendarIcon
          />
        </div>
      </div>
    );
  }
  return (
    <div
      className={`overflow-hidden border shadow-sm ${
        isDark ? "border-[#3A3A50] shadow-none" : "border-slate-200/90 "
      } ${cardBg}`}
    >
      <div className="px-1 sm:px-2 pt-2 pb-0 relative w-full min-w-0">
        {chartBusy && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-black/40">
            <ProgressSpinner style={{ width: 42, height: 42 }} />
          </div>
        )}
        <div className={`h-full w-full rounded-xl min-w-0 ${isDark ? "bg-[#14141c]" : "bg-white"}`}>
          <AssetValueLineChart
            data={assetLineChartData}
            loading={performanceLoading && !graphDataLength}
            height="365px"
            className="asset-chart w-full min-w-0"
            selectedChartFilter={assetChartFilter}
            selectedFilters={chartSeriesFilters}
            showProjections={showProjections}
            useBrandingColors
            seriesColorMap={seriesColorMap}
            seriesLabelMap={seriesLabelMap}
            firstLineSeriesExtras={combinedFirstLineExtras}
            appendSeries={trueIqAppendSeries}
            chartGridTop={chartGridTop}
            tooltipFormatterOverride={tooltipFormatterOverride}
            showActualValues={showActualValues}
          />
          <div className="mt-1 px-1 pb-2">
            <AssetValueChartFilter
              selectedChartFilter={assetChartFilter}
              onChartFilterChange={onAssetChartFilterChange}
              showProjections={showProjections}
              onProjectionsToggle={onProjectionsToggle}
              enableAgeFilter={enableAgeFilter}
              hideProjection
              overrideCalendarIcon
            />
          </div>
        </div>
      </div>
      {!isBuySell && (
        <div className={`flex items-center px-4 pb-3 gap-2 border-t ${divider}`}>
          <p className={`text-[12px] text-center flex-1 ${textSub}`}>
            Plan Funding for {insuredName}
          </p>
        </div>
      )}
    </div>
  );
});

PerformanceChartPanel.displayName = "PerformanceChartPanel";

export default PerformanceChartPanel;
